import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, ZoomControl, useMap } from "react-leaflet";
import { divIcon } from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import '../../utils/leaflet-setup';

import DirectionMapIcon from "./DirectionMapIcon.component";
import { useMapRef } from "../../context/MapRefContext.jsx";
import { useSelector } from "react-redux";

const MAPBOX_TOKEN = import.meta.env.VITE_APP_MAPBOX_TOKEN;
const MAPBOX_STYLE_URL = `https://api.mapbox.com/styles/v1/kingsaad98/ckl26gt3s0pkr17qnaiy071e3/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`;

// Map controller component to handle map interactions
function MapController({ route, activeDeviceId, devices, onMapReady }) {
  const map = useMap();
  const mapRef = useMapRef();
  
  useEffect(() => {
    if (map) {
      mapRef.current = map;
      onMapReady?.(map);
      
      if (route.length > 0 && !activeDeviceId) {
        const bounds = route.map((coord) => [coord[0], coord[1]]);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [map, route, activeDeviceId, onMapReady, mapRef]);

  useEffect(() => {
    if (!activeDeviceId || !devices || devices.length === 0) return;
    
    const activeDevice = devices.find((d) => d._id === activeDeviceId);
    if (!activeDevice?.locations?.[0]) return;

    const latest = activeDevice.locations[0];
    if (map) {
      const latlng = [Number(latest.lat), Number(latest.long)]; 
      map.flyTo(latlng, map.getZoom(), {
        animate: true,
        duration: 1,
      });
    }
  }, [activeDeviceId, devices, map]);

  return null;
}

function RouteMap({ route = [], geofences, children, onMapReady, className, ...props }) {
  const activeDeviceId = useSelector((state) => state.assets.IdDeviceToTrack);
  const devices = useSelector((state) => state.assets.devices);

  const defaultCenter = [0, 0];
  const defaultZoom = 2;

  const center = route.length > 0 && route[0]?.[0] && route[0]?.[1] 
    ? route[0] 
    : defaultCenter;

  return (
    <div className={className} style={{ position: 'relative', height: '100%', width: '100%' }}>
      <MapContainer
        {...props}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        center={center}
        zoom={route.length > 0 ? 13 : defaultZoom}
        zoomControl={false}
        tap={false}
        dragging={true}
        touchZoom={true}
        scrollWheelZoom={true}
      >
        <MapController 
          route={route} 
          activeDeviceId={activeDeviceId} 
          devices={devices}
          onMapReady={onMapReady}
        />
        {children}

        <ZoomControl position="bottomright" />

        <TileLayer
          attribution='© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={MAPBOX_STYLE_URL}
        />

        {route.map((coordinate, index) => (
          coordinate[0] &&
          coordinate[1] && (
            <Marker
              key={index}
              position={coordinate}
              icon={divIcon({
                html: renderToStaticMarkup(
                  <DirectionMapIcon color="#7fff00" angle={route.Course} />
                ),
                className: '',
              })}
            />
          )
        ))}
      </MapContainer>
    </div>
  );
}

export default RouteMap;
